/* @flow */
// Free plan allowance: accounts without Pro (trial ended, not subscribed)
// get FREE_MINUTES of music per hour. The hour starts at the first play
// in a window; once it has passed, a fresh allowance begins.
// Usage is kept per account in this browser.

export const FREE_MINUTES = 5
const FREE_MS = FREE_MINUTES * 60 * 1000
const WINDOW_MS = 60 * 60 * 1000
const KEY_PREFIX = 'gr_free_allowance_'

type Allowance = { windowStart: number, usedMs: number }

const storageKey = (userId: string) => KEY_PREFIX + (userId || 'anonymous')

const read = (userId: string): Allowance => {
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed.windowStart === 'number' && typeof parsed.usedMs === 'number') {
        return parsed
      }
    }
  } catch (e) {}
  return { windowStart: 0, usedMs: 0 }
}

const write = (userId: string, allowance: Allowance) => {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(allowance))
  } catch (e) {}
}

const current = (userId: string, now: number = Date.now()): Allowance => {
  const allowance = read(userId)
  if (!allowance.windowStart || now - allowance.windowStart >= WINDOW_MS) {
    return { windowStart: 0, usedMs: 0 }
  }
  return allowance
}

export const remainingMs = (userId: string, now: number = Date.now()): number => {
  return Math.max(0, FREE_MS - current(userId, now).usedMs)
}

export const msUntilReset = (userId: string, now: number = Date.now()): number => {
  const allowance = current(userId, now)
  if (!allowance.windowStart) return 0
  return Math.max(0, allowance.windowStart + WINDOW_MS - now)
}

// Record `elapsedMs` of listening; returns the remaining allowance.
export const consume = (userId: string, elapsedMs: number, now: number = Date.now()): number => {
  const allowance = current(userId, now)
  const next = {
    windowStart: allowance.windowStart || now,
    usedMs: Math.min(FREE_MS, allowance.usedMs + Math.max(0, elapsedMs))
  }
  write(userId, next)
  return Math.max(0, FREE_MS - next.usedMs)
}

export const formatClock = (ms: number): string => {
  const totalSeconds = Math.ceil(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}

export const formatMinutes = (ms: number): string => {
  const minutes = Math.max(1, Math.ceil(ms / 60000))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}
